import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, message, Divider, Typography } from 'antd';
import { LockOutlined, MailOutlined, UserOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth, googleProvider } from '../firebase/config';
import { siGoogle } from 'simple-icons';
import BrandIcon from '../components/BrandIcon';
import './Login.css';

const { Text } = Typography;

const STEPS = [
  { title: 'Create your account', text: 'Sign up with email or Google in seconds.' },
  { title: 'Verify your email', text: 'Tap the link we send to confirm it is you.' },
  { title: 'Shop and book', text: 'Buy products and book repairs with ease.' },
];

const Login = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const { login: authLogin, register, googleLogin, verify2fa } = useAuth();

  const [mode, setMode] = useState(location.pathname === '/signup' ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const isSignup = mode === 'signup';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === '1') {
      message.warning('Your session has expired. Please sign in again.');
      window.history.replaceState({}, document.title, '/login');
    }
  }, []);

  const goAfterAuth = () => {
    const from = location.state?.from || '/dashboard';
    navigate(from, { replace: true });
  };

  const switchMode = (next) => {
    setMode(next);
    form.resetFields();
    window.history.replaceState({}, '', next === 'signup' ? '/signup' : '/login');
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (isSignup) {
        const success = await register({
          full_name: values.full_name,
          email: values.email,
          password: values.password,
          password_confirmation: values.password,
          user_type: 'customer',
        });
        if (success) {
          message.success('Account created. Welcome to Current Care!');
          goAfterAuth();
        }
      } else {
        const result = await authLogin({ login: values.email, password: values.password });
        if (result && result.requires_2fa) {
          setUserId(result.user_id);
          setStep('verify');
        } else if (result) {
          message.success('Signed in successfully.');
          goAfterAuth();
        }
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onVerify2fa = async () => {
    if (!code || code.length !== 6) {
      message.warning('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const success = await verify2fa(userId, code);
      if (success) {
        goAfterAuth();
      }
    } catch (error) {
      message.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const gUser = result.user;
      const idToken = await gUser.getIdToken();
      const displayName = gUser.displayName || (gUser.email ? gUser.email.split('@')[0] : 'User');
      const success = await googleLogin({
        firebase_token: idToken,
        email: gUser.email,
        full_name: displayName,
        firebase_uid: gUser.uid,
        photo_url: gUser.photoURL,
        platform: 'web',
      });
      if (success) {
        message.success('Signed in with Google.');
        goAfterAuth();
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') return;
      message.error('Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="auth-page">
        <div className="auth-left">
          <div className="auth-form">
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <SecurityScanOutlined style={{ fontSize: 64, color: '#2d5a3d', marginBottom: 16 }} />
              <h1 className="auth-title">Two-Factor Authentication</h1>
              <p className="auth-sub">Enter the 6-digit code from your authenticator app.</p>
            </div>

            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              placeholder="000000"
              size="large"
              style={{
                textAlign: 'center',
                fontSize: 28,
                letterSpacing: 8,
                height: 56,
                borderRadius: 10,
                marginBottom: 16,
              }}
            />

            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              onClick={onVerify2fa}
              className="auth-submit"
            >
              Verify
            </Button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="link" onClick={() => { setStep('credentials'); setCode(''); setUserId(null); }}>
                Back to login
              </Button>
            </div>
          </div>
        </div>
        <div className="auth-right">
          <div className="auth-guide">
            <h2>Secure your account</h2>
            <p>Two-factor authentication adds an extra layer of security.</p>
            <div className="auth-flow">
              <div className="auth-flow-step">
                <div className="auth-flow-num"><SecurityScanOutlined /></div>
                <div className="auth-flow-body">
                  <b>Authenticator App</b>
                  <span>Use Google Authenticator, Authy, or any TOTP app.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-form">
          <Link to="/" className="auth-logo-link">
            <img src="/images/currentcare-logo.png" alt="Current Care" className="auth-logo" />
          </Link>
          <h1 className="auth-title">{isSignup ? 'Create your account' : 'Welcome back'}</h1>
          <p className="auth-sub">
            {isSignup
              ? 'Join Current Care in a few quick steps.'
              : 'Sign in to continue to Current Care.'}
          </p>

          <Button
            block
            size="large"
            onClick={handleGoogle}
            loading={googleLoading}
            className="auth-google"
            icon={!googleLoading && <BrandIcon icon={siGoogle} size={18} />}
          >
            Continue with Google
          </Button>

          <Divider className="auth-divider" plain>
            or
          </Divider>

          <Form form={form} layout="vertical" size="large" onFinish={onFinish} requiredMark={false}>
            {isSignup && (
              <Form.Item
                name="full_name"
                label="Full name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Ram Bahadur" />
              </Form.Item>
            )}
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="you@example.com" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter a password' },
                isSignup && { min: 6, message: 'Use at least 6 characters' },
              ].filter(Boolean)}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={isSignup ? 'Create a password' : 'Your password'}
              />
            </Form.Item>

            {!isSignup && (
              <div className="auth-row">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
                <Link to="/forgot-password" className="auth-link">
                  Forgot password?
                </Link>
              </div>
            )}

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              className="auth-submit"
            >
              {isSignup ? 'Create account' : 'Sign in'}
            </Button>
          </Form>

          <div className="auth-switch">
            {isSignup ? 'Already have an account?' : 'New to Current Care?'}
            <button type="button" onClick={() => switchMode(isSignup ? 'login' : 'signup')}>
              {isSignup ? 'Sign in' : 'Create an account'}
            </button>
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-guide">
          <h2>How it works</h2>
          <p>From sign up to service, in a few simple steps.</p>
          <div className="auth-flow">
            {STEPS.map((s, i) => (
              <div className="auth-flow-step" key={s.title}>
                <div className="auth-flow-num">{i + 1}</div>
                <div className="auth-flow-body">
                  <b>{s.title}</b>
                  <span>{s.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
